import axios from "axios";

const baseURL = process.env.REACT_APP_API_BASE_URL;

// ── In-flight request deduplication ──────────────────────────────────────────
// Problem: rapid UI interactions (e.g. double-clicking a button, StrictMode
// double-invocations) can trigger identical GET requests simultaneously,
// wasting bandwidth and causing race conditions in state updates.
//
// Fix: track in-flight request promises by a stable key; return the same
// promise to all callers until the request completes.  Only GET requests are
// deduplicated — mutating methods (POST/PUT/PATCH/DELETE) are always unique.
// ─────────────────────────────────────────────────────────────────────────────
const inFlightRequests = new Map();

const isAbsoluteUrl = (url = "") => /^https?:\/\//i.test(url);

const normalizeUrl = (config = {}) => {
  const requestUrl = config.url || "";
  if (!requestUrl) return "";
  if (isAbsoluteUrl(requestUrl)) return requestUrl;

  const requestBaseUrl = config.baseURL || baseURL || "";
  if (!requestBaseUrl) return requestUrl;

  return `${requestBaseUrl.replace(/\/+$/, "")}/${requestUrl.replace(/^\/+/, "")}`;
};

// Produces a deterministic string for any params/data value, including FormData.
const stableStringify = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    const entries = [];
    value.forEach((entryValue, entryKey) => {
      const safeValue = typeof entryValue === "string" ? entryValue : entryValue?.name || "[binary]";
      entries.push([entryKey, safeValue]);
    });
    entries.sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries);
  }

  if (typeof value === "string") return value;

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }

  if (typeof value === "object") {
    const sortedKeys = Object.keys(value).sort();
    const sortedObj = {};
    sortedKeys.forEach((key) => {
      sortedObj[key] = value[key];
    });
    return JSON.stringify(sortedObj);
  }

  return String(value);
};

const getRequestKey = (config = {}) => {
  const method = (config.method || "get").toUpperCase();
  const url = normalizeUrl(config);
  const params = stableStringify(config.params);
  const data = stableStringify(config.data);
  return `${method}::${url}::${params}::${data}`;
};

// Only deduplicate idempotent reads — never mutating methods.
const shouldSkipRequestLock = (config = {}) => {
  if (config.skipRequestLock === true) return true;
  const method = (config.method || "get").toUpperCase();
  return method !== "GET";
};

// ── Axios instance ────────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── 401 session-expiry handling ───────────────────────────────────────────────
// Show a non-blocking toast, clear local state, then redirect to login.
// The flag prevents multiple simultaneous 401 responses from triggering
// multiple redirects.
let sessionExpiredHandled = false;

const showSessionExpiredToast = () => {
  const existing = document.getElementById("session-expired-toast");
  if (existing) return;

  const el = document.createElement("div");
  el.id = "session-expired-toast";
  el.style.cssText = [
    "position:fixed",
    "top:20px",
    "right:20px",
    "background:#f59e0b",
    "color:#fff",
    "padding:14px 20px",
    "border-radius:8px",
    "z-index:99999",
    "font-family:sans-serif",
    "font-size:14px",
    "line-height:1.5",
    "box-shadow:0 4px 16px rgba(0,0,0,0.25)",
    "min-width:260px",
    "animation:slideIn 0.3s ease",
  ].join(";");

  el.innerHTML = `
    <strong style="display:block;font-size:15px;margin-bottom:4px">⚠️ Session Expired</strong>
    Your session has expired. Redirecting to login...
  `;

  const style = document.createElement("style");
  style.textContent =
    "@keyframes slideIn{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}";
  document.head.appendChild(style);
  document.body.appendChild(el);

  setTimeout(() => {
    el.style.transition = "opacity 0.3s ease";
    el.style.opacity = "0";
    setTimeout(() => el.remove(), 300);
  }, 2200);
};

// ── Request interceptor ───────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Let the browser set the correct multipart boundary for FormData uploads.
    // Keeping an explicit Content-Type: application/json breaks multipart requests.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — 401 handling ──────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes("login");
    if (
      error.response?.status === 401 &&
      !sessionExpiredHandled &&
      !isLoginRequest
    ) {
      sessionExpiredHandled = true;
      showSessionExpiredToast();
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => {
        window.location.replace("/");
      }, 2500);
    }
    return Promise.reject(error);
  }
);

// ── In-flight deduplication via request override ──────────────────────────────
// Overriding axiosInstance.request (rather than using an interceptor) is the
// only reliable way to return the *same promise* to duplicate callers —
// interceptors always create a new promise chain per invocation.
const originalRequest = axiosInstance.request.bind(axiosInstance);

axiosInstance.request = function requestWithInFlightLock(configOrUrl, config) {
  const requestConfig =
    typeof configOrUrl === "string"
      ? { ...(config || {}), url: configOrUrl }
      : configOrUrl || {};

  if (shouldSkipRequestLock(requestConfig)) {
    return originalRequest(requestConfig);
  }

  const requestKey = getRequestKey(requestConfig);

  if (inFlightRequests.has(requestKey)) {
    // Return the existing promise — both callers share the same response.
    return inFlightRequests.get(requestKey);
  }

  const requestPromise = originalRequest(requestConfig).finally(() => {
    inFlightRequests.delete(requestKey);
  });

  inFlightRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export default axiosInstance;
